import { useEffect, useState } from 'react'
import ApiClient from '../../backend/ApiClient'
import Helper from '../../helpers/Helper'
import TimeHelper from '../../helpers/TimeHelper'
import ActivityManager from '../../managers/ActivityManager'
import { useAppSelector } from '../../store/Hooks'
import SmartBadgeFilter from '../badge_filter/SmartBadgeFilter'
import { LineChart } from '../charts'
import SmartCard from '../controls/cards/SmartCard'
import ChartCard from '../controls/data_visualization/ChartCard'
import KPICard from '../controls/data_visualization/KPICard'
import RouteHeader from '../controls/RouteHeader'
import StaleObserver from '../controls/stale/StaleObserver'
import useQuery, { buildQueryKey } from '../hooks/useQuery'
import Tile from '../layout/Tile'
import RoutingManager from '../routing/Routing'

export default function FundsBreakdown() {
    const params = RoutingManager.getParams<{ contractId: number }>(useAppSelector(s => s.session.routeParams))
    const time = useAppSelector(s => s.app.timeSettings ?? TimeHelper.timeSettings())
    const [filter, setFilter] = useState({
        fundsId: []
    })

    const { status, response } = useQuery({
        queryKey: buildQueryKey('contract-funds', params.contractId, TimeHelper.stringify(time)),
        queryFn: () => ApiClient.getContractFunds(params.contractId, time),
        cache: true
    })

    useEffect(() => {
        if (status === 'success' && response && response.funds)
            setFilter({ fundsId: response?.funds?.map(x => x.id) })
    }, [status])

    const isLoading = status === 'loading'
    const funds = filter?.fundsId?.map(x => response?.funds?.find(k => k.id === x))

    return (
        <Tile.Container>
            <RouteHeader
                primary='Funds Breakdown'
                timeSettings={time}
                subActions={[
                    { label: 'Updated last week', icon: 'clock', onClick: () => ActivityManager.openActivity('funds') }
                ]}
            />
            <Tile.Row flex={0} spacing={2}>
                {(isLoading ? Array(3).fill(0) : response?.funds)?.map(fund => {
                    return (
                        <SmartCard flex={1} key={fund?.id}>
                            <KPICard
                                label={fund?.name ?? 'Fund' + ' Balance'}
                                unit='$'
                                isLoading={isLoading}
                                data={Helper.dataframeToKPICard(fund?.balances)}
                            />
                        </SmartCard>
                    )
                })}
            </Tile.Row>
            <Tile.Row flex={0} spacing={1}>
                <SmartBadgeFilter
                    name='Funds'
                    values={filter.fundsId}
                    contractId={params.contractId}
                    onApply={x => setFilter({ fundsId: x })}
                />
            </Tile.Row>
            <Tile.Tile flex={1}>
                <SmartCard>
                    <ChartCard
                        title='Balance'
                        chart={(
                            <LineChart
                                isLoading={isLoading || !funds || funds.length === 0}
                                data={{
                                    datasets: funds?.map(x => ({ data: x.balances.points.map(k => k.y), label: x.name })),
                                    labels: funds[0]?.balances?.points?.map(x => x.x)
                                }}
                            />
                        )}
                    />
                </SmartCard>
            </Tile.Tile>
            <StaleObserver status={status} />
        </Tile.Container>
    )
}
